import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PlaceCard from "../components/PlaceCard";
import FilterSelect from "../components/FilterSelect";
import SEO from "../components/SEO";

const CATEGORIES = [
    { value: "", label: "Toutes catégories" },
    { value: "restaurant", label: "Restaurants" },
    { value: "hotel", label: "Hôtels" },
    { value: "maison-hotes", label: "Maisons d'hôtes" },
    { value: "pub", label: "Pubs & bars" },
    { value: "cafe", label: "Cafés" },
    { value: "coffee-shop", label: "Coffee shops" },
    { value: "boulangerie", label: "Boulangeries" },
    { value: "patisserie", label: "Pâtisseries" },
    { value: "glacier", label: "Glaciers" },
    { value: "fromagerie", label: "Fromageries" },
    { value: "caviste", label: "Cavistes" },
    { value: "epicerie", label: "Épiceries fines" },
    { value: "fleuriste", label: "Fleuristes" },
    { value: "antiquaire", label: "Antiquaires" },
    { value: "design", label: "Boutiques design" },
    { value: "librairie", label: "Librairies" },
    { value: "musee", label: "Musées & galeries" },
    { value: "spa", label: "Spas" },
    { value: "jardin", label: "Jardins" },
    { value: "marche", label: "Marchés" },
    { value: "patrimoine", label: "Patrimoine" },
    { value: "villa", label: "Villas" },
    { value: "friche", label: "Friches" },
];

export default function Adresses() {
    const [params, setParams] = useSearchParams();
    const [places, setPlaces] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);

    const city = params.get("city") || "";
    const category = params.get("category") || "";
    const q = params.get("q") || "";

    useEffect(() => {
        api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        api.get("/places", { params: { city: city || undefined, category: category || undefined, q: q || undefined, limit: 500 } })
            .then((r) => setPlaces(r.data))
            .finally(() => setLoading(false));
    }, [city, category, q]);

    const update = (key, value) => {
        const next = new URLSearchParams(params);
        if (!value) next.delete(key);
        else next.set(key, value);
        setParams(next);
    };

    return (
        <div className="pt-28" data-testid="adresses-page">
            <SEO
                title="Adresses vérifiées — restaurants, hôtels, cafés, fromageries"
                description="289 adresses chic vérifiées entre Le Tréport, Mers-les-Bains, Cayeux-sur-Mer, Eu, Aumale et Neufchâtel-en-Bray. Note Google ≥ 4,5/5, ≥ 50 avis. Restaurants, hôtels, maisons d'hôtes, cavistes, fromagers, boulangeries — Normandie & Picardie."
                path="/adresses"
            />
            <section className="px-6 md:px-12 max-w-[1600px] mx-auto pt-16 md:pt-24 pb-16">
                <p className="cp-tiny text-cp-foret mb-8">Chapitre II · Adresses</p>
                <h1 className="cp-serif text-4xl sm:text-5xl md:text-[56px] lg:text-[72px] xl:text-[92px] leading-[1.02] text-cp-encre max-w-5xl tracking-[-0.025em]">
                    Tables, maisons,<br /><em className="cp-display-italic text-cp-foret">lieux</em> de grâce.
                </h1>
                <p className="cp-serif italic text-xl md:text-2xl font-light text-cp-gris leading-relaxed mt-12 max-w-3xl">
                    Une sélection resserrée des meilleures adresses du territoire — tables, hôtels discrets, cafés signés. Chaque adresse renvoie directement au site de l'établissement pour la réservation.
                </p>
            </section>

            {/* COMPACT FILTERS — dropdowns only */}
            <section className="px-6 md:px-12 max-w-[1600px] mx-auto py-5 border-y border-cp-border mb-12 sticky top-20 md:top-24 z-30 bg-cp-ivoire/95 backdrop-blur-xl" data-testid="adresses-filters">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 items-end">
                    <FilterSelect
                        label="Ville"
                        value={city}
                        onChange={(v) => update("city", v)}
                        testId="filter-city"
                        options={[{ value: "", label: "Toutes les villes" }, ...cities.map((c) => ({ value: c.slug, label: c.name }))]}
                    />
                    <FilterSelect
                        label="Catégorie"
                        value={category}
                        onChange={(v) => update("category", v)}
                        testId="filter-category"
                        options={CATEGORIES}
                    />
                    <div className="col-span-2 lg:col-span-3">
                        <span className="cp-kicker text-[9px] text-cp-gris mb-1.5 block">Recherche</span>
                        <input
                            type="text"
                            placeholder="Nom, cuisine, ambiance…"
                            value={q}
                            onChange={(e) => update("q", e.target.value)}
                            data-testid="adresses-search"
                            className="w-full px-4 py-2.5 border border-cp-border bg-cp-ivoire cp-serif text-sm md:text-base text-cp-encre focus:border-cp-foret outline-none transition-colors"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                    <p className="cp-tiny text-cp-gris">
                        {places.length} adresse{places.length > 1 ? "s" : ""}
                        {(city || category || q) && (
                            <button onClick={() => { ["city","category","q"].forEach(k=>update(k,"")); }} className="ml-3 text-cp-foret hover:underline cp-serif italic" data-testid="filter-reset">Tout effacer ×</button>
                        )}
                    </p>
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1600px] mx-auto pb-32">
                {loading ? (
                    <p className="text-cp-gris">Chargement…</p>
                ) : places.length === 0 ? (
                    <p className="text-cp-gris italic text-lg">Aucune adresse ne correspond à ces filtres.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
                        {places.map((p, i) => <PlaceCard key={p.id} place={p} index={i} />)}
                    </div>
                )}
            </section>
        </div>
    );
}
